'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserStreak } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { logger } from '@/lib/telemetry/logger';
import { cn } from '@/lib/ui/utils';
import { useStreakDay } from './use-streak-day';
interface StreakCalendarSheetProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function StreakCalendarSheet(t0) {
  const $ = _c(35);
  const {
    userId,
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("streaks");
  const locale = useLocale();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["currentStreak", "longestStreak", "timezone", {
      days: ["date", "traded"]
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== userId) {
    t2 = {
      identifier: userId,
      fields: t1
    };
    $[1] = userId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== open) {
    t3 = {
      enabled: open
    };
    $[3] = open;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data,
    error
  } = useGetUserStreak(t2, t3);
  let t4;
  let t5;
  if ($[5] !== error || $[6] !== open || $[7] !== userId) {
    t4 = () => {
      if (open && error) {
        logger.error("streak.activity_load_failed", {
          userId,
          error: String(error)
        });
      }
    };
    t5 = [error, open, userId];
    $[5] = error;
    $[6] = open;
    $[7] = userId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let T0;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== data || $[11] !== error || $[12] !== locale || $[13] !== onOpenChange || $[14] !== open || $[15] !== t) {
    const zone = data ? zoneFormatter(locale, data.timezone).formatToParts(0).find(_temp)?.value ?? data.timezone : null;
    T0 = InfoModal;
    t6 = open;
    t7 = onOpenChange;
    t8 = "default";
    if ($[22] !== t) {
      t9 = t("activity_title");
      $[22] = t;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t10 = zone === null ? error ? t("activity_error") : t("activity_loading") : t("account_day_boundary", {
      zone
    });
    $[10] = data;
    $[11] = error;
    $[12] = locale;
    $[13] = onOpenChange;
    $[14] = open;
    $[15] = t;
    $[16] = T0;
    $[17] = t10;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[16];
    t10 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[24] !== data || $[25] !== open) {
    t11 = open && data ? <StreakActivityCalendar timezone={data.timezone} currentStreak={data.currentStreak} longestStreak={data.longestStreak} days={data.days} /> : null;
    $[24] = data;
    $[25] = open;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== T0 || $[28] !== t10 || $[29] !== t11 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t12 = <T0 open={t6} onOpenChange={t7} size={t8} title={t9} description={t10}>{t11}</T0>;
    $[27] = T0;
    $[28] = t10;
    $[29] = t11;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp(part) {
  return part.type === "timeZoneName";
}
interface StreakActivityCalendarProps {
  timezone: string;
  currentStreak: number;
  longestStreak: number;
  days: readonly {
    date: string;
    traded: boolean;
  }[];
}
function StreakActivityCalendar(t0) {
  const $ = _c(101);
  const {
    timezone,
    currentStreak,
    longestStreak,
    days
  } = t0;
  const t = useTranslations("streaks");
  const locale = useLocale();
  const today = useStreakDay(timezone);
  const [monthOffset, setMonthOffset] = useState(0);
  if (today === null) {
    let t1;
    if ($[0] !== t) {
      t1 = t("activity_loading");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <p className="text-caption text-foreground-secondary">{t1}</p>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  let todayIndex;
  if ($[4] !== days || $[5] !== today) {
    todayIndex = monthIndexOf(today);
    t1 = days.reduce(_temp2, todayIndex);
    $[4] = days;
    $[5] = today;
    $[6] = t1;
    $[7] = todayIndex;
  } else {
    t1 = $[6];
    todayIndex = $[7];
  }
  const minIndex = t1;
  const minOffset = minIndex - todayIndex;
  const offset = Math.min(0, Math.max(minOffset, monthOffset));
  const viewedIndex = todayIndex + offset;
  const year = Math.floor(viewedIndex / 12);
  const month = viewedIndex % 12;
  let t2;
  if ($[8] !== locale || $[9] !== month || $[10] !== year) {
    t2 = monthFormatter(locale).format(new Date(year, month, 1));
    $[8] = locale;
    $[9] = month;
    $[10] = year;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const monthLabel = t2;
  let t3;
  let weekdays;
  if ($[12] !== locale || $[13] !== month || $[14] !== year) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let t4;
    if ($[17] !== month || $[18] !== year) {
      t4 = new Date(year, month, 1).getDay();
      $[17] = month;
      $[18] = year;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    const firstWeekday = (t4 + 6) % 7;
    let t5;
    if ($[20] !== locale) {
      const weekdayFormatter = weekdayFormatterFor(locale);
      t5 = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((key, i) => ({
        key,
        label: weekdayFormatter.format(new Date(2024, 0, 1 + i))
      }));
      $[20] = locale;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    weekdays = t5;
    let t6;
    if ($[22] !== firstWeekday) {
      t6 = Array.from({
        length: firstWeekday
      }, _temp3);
      $[22] = firstWeekday;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== month || $[25] !== year) {
      t7 = (__0, i_1) => ({
        key: `${year}-${String(month + 1).padStart(2, "0")}-${String(i_1 + 1).padStart(2, "0")}`,
        day: i_1 + 1 as number | null
      });
      $[24] = month;
      $[25] = year;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t3 = [...t6, ...Array.from({
      length: daysInMonth
    }, t7)];
    $[12] = locale;
    $[13] = month;
    $[14] = year;
    $[15] = t3;
    $[16] = weekdays;
  } else {
    t3 = $[15];
    weekdays = $[16];
  }
  const cells = t3;
  let t4;
  if ($[27] !== days) {
    t4 = new Set(days.flatMap(_temp4));
    $[27] = days;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const traded = t4;
  const todayTraded = traded.has(today);
  let t5;
  if ($[29] !== viewedIndex) {
    t5 = [viewedIndex];
    $[29] = viewedIndex;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== currentStreak || $[32] !== t) {
    t6 = t("day_streak", {
      count: currentStreak
    });
    $[31] = currentStreak;
    $[32] = t;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== t6) {
    t7 = <span>{t6}</span>;
    $[34] = t6;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== longestStreak || $[37] !== t) {
    t8 = t("best_label", {
      count: longestStreak
    });
    $[36] = longestStreak;
    $[37] = t;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== t8) {
    t9 = <span>{t8}</span>;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t7 || $[42] !== t9) {
    t10 = <div className="flex flex-wrap justify-between gap-2 text-body text-foreground-secondary">{t7}{t9}</div>;
    $[41] = t7;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t) {
    t11 = t("prev_month");
    $[44] = t;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  const t12 = offset <= minOffset;
  let t13;
  if ($[46] !== offset) {
    t13 = () => setMonthOffset(offset - 1);
    $[46] = offset;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon icon={ArrowLeft01Icon} className="size-icon-md" />;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t12 || $[51] !== t13) {
    t15 = <AnimatedButton variant="tertiary" size="sm" aria-label={t11} disabled={t12} onClick={t13}>{t14}</AnimatedButton>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== monthLabel) {
    t16 = <div className="text-heading-xs">{monthLabel}</div>;
    $[53] = monthLabel;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== t) {
    t17 = t("next_month");
    $[55] = t;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const t18 = offset >= 0;
  let t19;
  if ($[57] !== offset) {
    t19 = () => setMonthOffset(offset + 1);
    $[57] = offset;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Icon icon={ArrowRight01Icon} className="size-icon-md" />;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t19) {
    t21 = <AnimatedButton variant="tertiary" size="sm" aria-label={t17} disabled={t18} onClick={t19}>{t20}</AnimatedButton>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t15 || $[65] !== t16 || $[66] !== t21) {
    t22 = <div className="flex items-center justify-between">{t15}{t16}{t21}</div>;
    $[64] = t15;
    $[65] = t16;
    $[66] = t21;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== weekdays) {
    t23 = weekdays.map(_temp5);
    $[68] = weekdays;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== cells || $[71] !== t || $[72] !== today || $[73] !== traded) {
    t24 = cells.map(cell => {
      if (cell.day === null) {
        return <span key={cell.key} />;
      }
      const isTraded = traded.has(cell.key);
      const isToday = cell.key === today;
      return <span key={cell.key} role="img" aria-current={isToday ? "date" : undefined} aria-label={`${cell.key}${isTraded ? ` ${t("legend_traded")}` : ""}`} className={cn("grid aspect-square place-items-center rounded-panel-inset border text-caption", isTraded ? "bg-feedback-success-surface text-feedback-success-content" : cell.key > today ? "text-foreground-disabled" : "bg-background-tertiary text-foreground-secondary", isToday ? isTraded ? "border-feedback-success-border" : "border-border-primary" : "border-transparent")}>{cell.day}</span>;
    });
    $[70] = cells;
    $[71] = t;
    $[72] = today;
    $[73] = traded;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t23 || $[76] !== t24) {
    t25 = <div className="grid grid-cols-7 gap-1.5">{t23}{t24}</div>;
    $[75] = t23;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="size-2.5 rounded-item bg-feedback-success-surface" aria-hidden={true} />;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== t) {
    t27 = t("legend_traded");
    $[79] = t;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t27) {
    t28 = <span className="inline-flex items-center gap-1.5">{t26}{t27}</span>;
    $[81] = t27;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  const t29 = todayTraded ? "border-feedback-success-border bg-feedback-success-surface" : "border-border-primary bg-background-tertiary";
  let t30;
  if ($[83] !== t29) {
    t30 = cn("size-2.5 rounded-item border", t29);
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t30) {
    t31 = <span className={t30} aria-hidden={true} />;
    $[85] = t30;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== t) {
    t32 = t("legend_today");
    $[87] = t;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t31 || $[90] !== t32) {
    t33 = <span className="inline-flex items-center gap-1.5">{t31}{t32}</span>;
    $[89] = t31;
    $[90] = t32;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== t28 || $[93] !== t33) {
    t34 = <div className="flex flex-wrap gap-4 text-micro text-foreground-secondary">{t28}{t33}</div>;
    $[92] = t28;
    $[93] = t33;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  let t35;
  if ($[95] !== t10 || $[96] !== t22 || $[97] !== t25 || $[98] !== t34 || $[99] !== t5) {
    t35 = <MotionAnimatedAutoHeight innerClassName="flex flex-col gap-4" layoutDependencies={t5}>{t10}{t22}{t25}{t34}</MotionAnimatedAutoHeight>;
    $[95] = t10;
    $[96] = t22;
    $[97] = t25;
    $[98] = t34;
    $[99] = t5;
    $[100] = t35;
  } else {
    t35 = $[100];
  }
  return t35;
}
function _temp5(day_1) {
  return <span key={day_1.key} className="text-center text-caption text-foreground-tertiary">{day_1.label}</span>;
}
function _temp4(day_0) {
  return day_0.traded ? [day_0.date] : [];
}
function _temp3(_, i_0) {
  return {
    key: `pad-${i_0}`,
    day: null as number | null
  };
}
function _temp2(min, day) {
  return Math.min(min, monthIndexOf(day.date));
}
function monthIndexOf(dateStr: string): number {
  const [year, month] = dateStr.split('-');
  return Number(year) * 12 + Number(month) - 1;
}
function monthFormatter(locale: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  });
}
function weekdayFormatterFor(locale: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'narrow'
  });
}
function zoneFormatter(locale: string, timezone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName: 'longGeneric'
  });
}